import { useEffect, useRef, useState } from 'react';
import Header from "./components/header/Header";
import './App.css';
import { withAuthenticator } from '@aws-amplify/ui-react';
import "@aws-amplify/ui-react/styles.css";
import Navigation from './components/header/Navigation';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import FiltersPanel from './components/filters/FiltersPanel';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';

dayjs.extend(utc);

// Dashboard tab key constants
const CHAT_TAB_KEY = 'Chat';

function DashboardEmbed({ signOut, user }) {
  
  const containerRefs = useRef({});
  const chatContainerRef = useRef(null);
  const chatIframeRef = useRef(null);
  const [cachedEmbedUrls, setCachedEmbedUrls] = useState({});
  const [loadedDashboards, setLoadedDashboards] = useState(new Set());
  const [embeddedDashboards, setEmbeddedDashboards] = useState({});
  const [isDashboardReady, setIsDashboardReady] = useState({});
  const [lastAppliedFilters, setLastAppliedFilters] = useState({});
  
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [datesInitialized, setDatesInitialized] = useState(false);
  const theme = createTheme();
  const [filters, setFilters] = useState(null);
  const [selectedHashtag, setSelectedHashtag] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState([]);
  const [selectedAge, setSelectedAge] = useState([]);
  const [selectedGender, setSelectedGender] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState([]);
  const [selectedRankPosition, setSelectedRankPosition] = useState(0);
  const [selectedDateRange, setSelectedDateRange] = useState([null, null]);
  const [selectedDatehistory, setSelectedDatehistory] = useState([]);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [selectedDateLabel, setSelectedDateLabel] = useState('');
  
  const [hashtagFilterEnglish, setHashtagFilterEnglish] = useState(true);
  const [hashtagFilterLetters, setHashtagFilterLetters] = useState(false);
  const [hashtagFilterNumbers, setHashtagFilterNumbers] = useState(false);
  const [hashtagFilterEmojis, setHashtagFilterEmojis] = useState(false);
  const [isFiltersPanelCollapsed, setIsFiltersPanelCollapsed] = useState(false);


  const dashboardIdMap = {
    Overview: '486f3ff8-c86e-4498-9e32-61c2a53c180f',
    Details: 'dd6dd614-4b10-4090-8d91-26a2b73359db',
    Compare: 'f53f98f4-0bc7-4668-bffd-7230e18b7746',
    Content: '3ef27204-424f-4b78-b200-980e7e39f45b',
    Segments: 'c461e65d-7a3f-498f-882d-c1c5290a96b6',
    Audience: '796ec552-b033-4688-86f7-eb48f56ca5bf',
    'A.I. Chat': 'chat-iframe',
  };


  const [activeDashboardId, setActiveDashboardId] = useState('Overview');
  const embedUrl = `${process.env.REACT_APP_EMBED_API}/default/cultural_fluency_embedurl/dashboard:${dashboardIdMap[activeDashboardId]}`;
  const filtersUrl = `${process.env.REACT_APP_FILTERS_API}/default/cultural_fluency_filters`;


  useEffect(() => {
    Object.keys(dashboardIdMap).forEach(dashboardName => {
      if (!containerRefs.current[dashboardName]) {
        containerRefs.current[dashboardName] = { current: null };
      }
    });
  }, []);

  // Prevent scroll-into-view behavior when clicking inside chat iframe
  useEffect(() => {
    const preventScroll = (e) => {
      // Lock the scroll position
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    // Listen for scroll events and immediately reset
    window.addEventListener('scroll', preventScroll, { passive: false });
    document.addEventListener('scroll', preventScroll, { passive: false });

    return () => {
      window.removeEventListener('scroll', preventScroll);
      document.removeEventListener('scroll', preventScroll);
    };
  }, []);

  // Remove the resize observer - the chat iframe should maintain constant height
  // based on its container, not resize based on internal content
  useEffect(() => {
    // No longer dynamically adjusting iframe height
    // The iframe will use 100% of its container height via CSS
  }, [filters]);

  const getCategoryForSubcategory = (subcategoryValue) => {
    if (!filters?.Subcategory?.optionsByCategory) return null;
    
    for (const [categoryValue, subcategoryOptions] of Object.entries(filters.Subcategory.optionsByCategory)) {
      if (subcategoryOptions.some(option => option.value === subcategoryValue)) {
        return categoryValue;
      }
    }
    return null;
  };

  const getAllSubcategories = () => {
    if (!filters?.Subcategory?.optionsByCategory) return [];
    
    const allSubcategories = [];
    Object.values(filters.Subcategory.optionsByCategory).forEach(categorySubcategories => {
      allSubcategories.push(...categorySubcategories);
    });
    
    const uniqueSubcategories = allSubcategories.filter((subcategory, index, self) => 
      index === self.findIndex(s => s.value === subcategory.value)
    );
    
    return uniqueSubcategories;
  };

  const getFilteredSegmentOptions = () => {
    if (!filters?.Segment?.options) return [];
    
    // If no categories or subcategories selected, show all segments
    if (selectedCategory.length === 0 && selectedSubcategory.length === 0) {
      return filters.Segment.options;
    }
    
    // Filter segments based on selected categories and subcategories
    // Assuming segments have optionsByCategory similar to subcategories
    if (filters.Segment.optionsByCategory) {
      const filteredSegments = [];
      
      // Add segments from selected categories
      selectedCategory.forEach(categoryValue => {
        const categorySegments = filters.Segment.optionsByCategory[categoryValue] || [];
        filteredSegments.push(...categorySegments);
      });
      
      // Add segments from selected subcategories if exists
      if (filters.Segment.optionsBySubcategory) {
        selectedSubcategory.forEach(subcatValue => {
          const subcatSegments = filters.Segment.optionsBySubcategory[subcatValue] || [];
          filteredSegments.push(...subcatSegments);
        });
      }
      
      // Remove duplicates
      const uniqueSegments = filteredSegments.filter((segment, index, self) => 
        index === self.findIndex(s => s.value === segment.value)
      );
      
      return uniqueSegments.length > 0 ? uniqueSegments : filters.Segment.options;
    }
    
    return filters.Segment.options;
  };

  const containsLetters = (text) => {
    if (!text) return false;
    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const code = char.charCodeAt(0);
      
      if ((code >= 65 && code <= 90) || (code >= 97 && code <= 122)) {
        continue;
      }
      
      if (code >= 48 && code <= 57) {
        continue;
      }
      
      if (code < 128) {
        continue;
      }
      
      if (/\p{L}/u.test(char) && !containsEmojis(char)) {
        return true;
      }
    }
    return false;
  };
  
  const containsNumbers = (text) => {
    if (!text) return false;
    return /[0-9]/.test(text);
  };
  
  const containsEmojis = (text) => {
    if (!text) return false;
    const emojiRegex = /[\u{1F000}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F300}-\u{1F5FF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F900}-\u{1F9FF}]/u;
    return emojiRegex.test(text);
  };

  const containsEnglish = (text) => {
    if (!text) return false;
    return /[a-zA-Z]/.test(text);
  };

  const getFilteredHashtagOptions = () => {
    if (!filters?.Hashtag?.options || !Array.isArray(filters.Hashtag.options)) {
      return [];
    }
    
    const filteredOptions = filters.Hashtag.options.filter((option) => {
      const text = option?.label || option?.value || '';
      
      if (!text) {
        return false;
      }
      
      const hasLetters = containsLetters(text);
      const hasNumbers = containsNumbers(text);
      const hasEmojis = containsEmojis(text);
      const hasEnglish = containsEnglish(text);
      
      if (hashtagFilterLetters && !hasLetters) return false;
      if (!hashtagFilterLetters && hasLetters) return false;
      
      if (hashtagFilterNumbers && !hasNumbers) return false;
      if (!hashtagFilterNumbers && hasNumbers) return false;
      
      if (hashtagFilterEmojis && !hasEmojis) return false;
      if (!hashtagFilterEmojis && hasEmojis) return false;
      
      if (hashtagFilterEnglish && !hasEnglish) return false;
      if (!hashtagFilterEnglish && hasEnglish) return false;
      
      return true;
    });
    
    return filteredOptions;
  };

  const getCurrentFilterState = () => {
    return {
      selectedCategory,
      selectedSubcategory,
      selectedHashtag,
      selectedLocation,
      selectedAge,
      selectedGender,
      selectedOpportunity,
      selectedSegment,
      selectedRankPosition,
      selectedDatehistory,
      customStartDate: customStartDate?.format('YYYY-MM-DD'),
      customEndDate: customEndDate?.format('YYYY-MM-DD'),
      hashtagFilterEnglish,
      hashtagFilterLetters,
      hashtagFilterNumbers,
      hashtagFilterEmojis
    };
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/amazon-quicksight-embedding-sdk@2.10.1/dist/quicksight-embedding-js-sdk.min.js';
        script.async = true;
        script.crossOrigin = 'anonymous';
        
        script.onload = () => {
          console.log("✅ QuickSight SDK loaded");
        };
        
        document.title = 'Cultural Fluency';
        document.body.appendChild(script);
        
        const loadedFilters = await fetchFilters();
        if (loadedFilters) {
          setFilters(loadedFilters);
          await setDefaultDates(loadedFilters);
        }
      } catch (error) {
        console.error("❌ Error during app initialization:", error);
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    if (datesInitialized) {
      console.log("📅 Dates initialized, loading dashboard...");
      
      const checkSDKAndLoad = (attempts = 0) => {
        const maxAttempts = 20;
        
        if (window.QuickSightEmbedding) {
          console.log("✅ SDK available, loading dashboard...");
          setTimeout(() => {
            loadDashboard(activeDashboardId);
          }, 500);
        } else if (attempts < maxAttempts) {
          setTimeout(() => checkSDKAndLoad(attempts + 1), 500);
        } else {
          console.error("❌ SDK failed to load");
        }
      };
      
      checkSDKAndLoad();
    }
  }, [datesInitialized]);

  const setDefaultDates = async (loadedFilters) => {
    const prior30Days = loadedFilters?.DateRange?.options?.find(
      opt => opt.label === "Prior 30 Days" || opt.value === "Prior 30 Days"
    );
    
    if (prior30Days && prior30Days.start && prior30Days.end) {
      console.log('Setting initial dates to Prior 30 Days');
      setSelectedDatehistory([prior30Days.value]);
      const start = dayjs.utc(prior30Days.start + 'T00:00:00');
      const end = dayjs.utc(prior30Days.end + 'T00:00:00');
      setSelectedDateRange([start, end]);
      setCustomStartDate(start);
      setCustomEndDate(end);
      
      setTimeout(() => {
        setDatesInitialized(true);
      }, 100);
      
    } else if (loadedFilters?.DateRange?.defaultStart && loadedFilters?.DateRange?.defaultEnd) {
      const start = dayjs.utc(loadedFilters.DateRange.defaultStart + 'T00:00:00');
      const end = dayjs.utc(loadedFilters.DateRange.defaultEnd + 'T00:00:00');
      setSelectedDateRange([start, end]);
      setCustomStartDate(start);
      setCustomEndDate(end);
      
      setTimeout(() => {
        setDatesInitialized(true);
      }, 100);
    } else {
      setDatesInitialized(true);
    }
  };

  async function fetchFilters() {
    try {
      const response = await fetch(filtersUrl);
      const data = await response.json();
      if (typeof data === 'object' && data !== null && !data.body) return data;
      if (typeof data.body === 'string') return JSON.parse(data.body);
      return null;
    } catch (error) {
      console.error('Error fetching filters:', error);
      return null;
    }
  }

  const loadDashboard = async (dashboardName = activeDashboardId) => {
    try {
      console.log('Loading dashboard:', dashboardName);
      
      if (cachedEmbedUrls[dashboardName]) {
        await embedDashboard(cachedEmbedUrls[dashboardName], dashboardName);
        return;
      }
      
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      const idToken = session.tokens.idToken.toString(); 
      const var_payload = JSON.parse(atob(idToken.split('.')[1]));

      const var_user_info = {
        userid: var_payload.sub,
        email: var_payload.email,
      };

      const currentEmbedUrl = `${process.env.REACT_APP_EMBED_API}/default/cultural_fluency_embedurl/dashboard:${dashboardIdMap[dashboardName]}`;
      
      const response = await fetch(currentEmbedUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(var_user_info)
      });

      const json = await response.json();
      if (!json.EmbedUrl) throw new Error("EmbedUrl not found");
      
      setCachedEmbedUrls(prev => ({
        ...prev,
        [dashboardName]: json.EmbedUrl
      }));
      
      await embedDashboard(json.EmbedUrl, dashboardName);
      
    } catch (e) {
      console.error("Error loading dashboard:", e);
    }
  };

  const embedDashboard = async (url, dashboardName = activeDashboardId) => {
    if (!window.QuickSightEmbedding) {
      setTimeout(() => embedDashboard(url, dashboardName), 1000);
      return;
    }

    const containerRef = containerRefs.current[dashboardName];
    if (!containerRef?.current) {
      setTimeout(() => embedDashboard(url, dashboardName), 500);
      return;
    }

    try {
      console.log(`📊 Embedding dashboard: ${dashboardName}`);
      
      if (loadedDashboards.has(dashboardName)) {
        console.log(`✅ Dashboard ${dashboardName} already embedded`);
        return;
      }
      
      containerRef.current.innerHTML = '';
      
      const { createEmbeddingContext } = window.QuickSightEmbedding;
      const embeddingContext = await createEmbeddingContext();
      
      // *** KEY FIX: Build parameters with ALL current filter values ***
      const initialParameters = [];
      
      // ALWAYS add date parameters - critical for initial load
      if (customStartDate && customEndDate) {
        initialParameters.push(
          { Name: 'datestart', Values: [customStartDate.format('YYYY-MM-DD')] },
          { Name: 'dateend', Values: [customEndDate.format('YYYY-MM-DD')] }
        );
        console.log('Setting initial dates:', { 
          start: customStartDate.format('YYYY-MM-DD'), 
          end: customEndDate.format('YYYY-MM-DD') 
        });
      }
      
      // Add all other filters
      initialParameters.push(
        { Name: 'categoryname', Values: selectedCategory.length > 0 ? selectedCategory : ['ALL_VALUES'] },
        { Name: 'subcategory', Values: selectedSubcategory.length > 0 ? selectedSubcategory : ['ALL_VALUES'] },
        { Name: 'hashtag', Values: selectedHashtag.length > 0 ? selectedHashtag.map(val => getLabelByValue('Hashtag', val)) : ['ALL_VALUES'] },
        { Name: 'location', Values: selectedLocation.length > 0 ? selectedLocation : ['ALL_VALUES'] },
        { Name: 'opportunityscore', Values: selectedOpportunity.length > 0 ? selectedOpportunity.map(val => getLabelByValue('OpportunityScore', val)) : ['ALL_VALUES'] },
        { Name: 'segment', Values: selectedSegment.length > 0 ? selectedSegment : ['ALL_VALUES'] },
        { Name: 'rankposition', Values: selectedRankPosition ? [selectedRankPosition.toString()] : ['ALL_VALUES'] },
        { Name: 'Datehistory', Values: selectedDatehistory.length > 0 ? selectedDatehistory : ['ALL_VALUES'] }
      );
      
      console.log('Initial parameters:', initialParameters);
      
      const frameOptions = {
        url: url,
        container: containerRef.current,
        scrolling: 'no',
        height: 'AutoFit',
        width: '100%',
        resizeHeightOnSizeChangedEvent: true,
        parameters: initialParameters, // Apply immediately
        onChange: (event) => {
          if (event.eventName === 'FRAME_LOADED') {
            console.log(`✅ Dashboard loaded: ${dashboardName}`);
            setIsDashboardReady(prev => ({ ...prev, [dashboardName]: true }));
            
            if (isInitialLoad && dashboardName === activeDashboardId) {
              setIsInitialLoad(false);
            }
          }
        }
      };
      
      const dashboard = await embeddingContext.embedDashboard(frameOptions);
      
      setEmbeddedDashboards(prev => ({ ...prev, [dashboardName]: dashboard }));
      setLoadedDashboards(prev => new Set([...prev, dashboardName]));
      
    } catch (error) {
      console.error(`❌ Error embedding ${dashboardName}:`, error);
    }
  };

  const getLabelByValue = (filterType, value) => {
    const filter = filters?.[filterType];
    if (!filter?.options) return value;
    const option = filter.options.find(opt => opt.value === value);
    return option?.label || value;
  };

  const updateQSParameter = (paramName, values, dashboardName = activeDashboardId) => {
    const dashboard = embeddedDashboards[dashboardName];
    
    if (!dashboard) {
      console.log(`Dashboard ${dashboardName} not ready`);
      return false;
    }

    try {
      const paramValues = Array.isArray(values) ? values : [values];
      const finalValues = paramValues.length === 0 ? ['ALL_VALUES'] : paramValues;
      
      dashboard.setParameters([{
        Name: paramName,
        Values: finalValues
      }]);
      
      console.log(`Updated ${paramName} on ${dashboardName}:`, finalValues);
      return true;
    } catch (error) {
      console.error(`Error updating ${paramName} on ${dashboardName}:`, error);
      return false;
    }
  };

  const handleCategoryChange = (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
    setSelectedCategory(selected);
    
    const orphanedSubcategories = selectedSubcategory.filter(subcatValue => {
      const belongsToSelectedCategory = selected.some(catValue => {
        const categorySubcats = filters?.Subcategory?.optionsByCategory?.[catValue] || [];
        return categorySubcats.some(subcat => subcat.value === subcatValue);
      });
      return !belongsToSelectedCategory;
    });

    if (orphanedSubcategories.length > 0) {
      const remainingSubcategories = selectedSubcategory.filter(val => !orphanedSubcategories.includes(val));
      setSelectedSubcategory(remainingSubcategories);
      
      loadedDashboards.forEach(dashboardName => {
        updateQSParameter("subcategory", remainingSubcategories, dashboardName);
      });
    }
    
    // Clear segments that don't belong to selected categories
    if (filters?.Segment?.optionsByCategory && selected.length > 0) {
      const validSegments = [];
      selected.forEach(catValue => {
        const catSegments = filters.Segment.optionsByCategory[catValue] || [];
        validSegments.push(...catSegments.map(s => s.value));
      });
      
      const remainingSegments = selectedSegment.filter(segVal => validSegments.includes(segVal));
      if (remainingSegments.length !== selectedSegment.length) {
        setSelectedSegment(remainingSegments);
        const segmentLabels = remainingSegments.map(val => getLabelByValue('Segment', val));
        loadedDashboards.forEach(dashboardName => {
          updateQSParameter("segment", segmentLabels, dashboardName);
        });
      }
    }
    
    loadedDashboards.forEach(dashboardName => {
      updateQSParameter("categoryname", selected, dashboardName);
    });
  };

  const onHashtagChange = (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
    setSelectedHashtag(selected);
    
    const hashtagLabels = selected.map(val => getLabelByValue('Hashtag', val));
    
    loadedDashboards.forEach(dashboardName => {
      // For Compare tab, only apply first 2 hashtags
      if (dashboardName === 'Compare') {
        updateQSParameter("hashtag", hashtagLabels.slice(0, 2), dashboardName);
      } else {
        updateQSParameter("hashtag", hashtagLabels, dashboardName);
      }
    });
  };

  const onLocationChange = (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
    setSelectedLocation(selected);
    
    loadedDashboards.forEach(dashboardName => {
      updateQSParameter("location", selected, dashboardName);
    });
  };

  const onSubcategoryChange = (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
    setSelectedSubcategory(selected);
    
    // Clear segments that don't belong to selected subcategories
    if (filters?.Segment?.optionsBySubcategory && selected.length > 0) {
      const validSegments = [];
      selected.forEach(subcatValue => {
        const subcatSegments = filters.Segment.optionsBySubcategory[subcatValue] || [];
        validSegments.push(...subcatSegments.map(s => s.value));
      });
      
      const remainingSegments = selectedSegment.filter(segVal => validSegments.includes(segVal));
      if (remainingSegments.length !== selectedSegment.length) {
        setSelectedSegment(remainingSegments);
        const segmentLabels = remainingSegments.map(val => getLabelByValue('Segment', val));
        loadedDashboards.forEach(dashboardName => {
          updateQSParameter("segment", segmentLabels, dashboardName);
        });
      }
    }
    
    loadedDashboards.forEach(dashboardName => {
      updateQSParameter("subcategory", selected, dashboardName);
    });
  };

  const onAgeChange = (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
    setSelectedAge(selected);
    
    loadedDashboards.forEach(dashboardName => {
      updateQSParameter("age", selected, dashboardName);
    });
  };

  const onGenderChange = (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
    setSelectedGender(selected);
    
    loadedDashboards.forEach(dashboardName => {
      updateQSParameter("gender", selected, dashboardName);
    });
  };

  const onOpportunityChange = (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
    setSelectedOpportunity(selected);
    
    const opportunityLabels = selected.map(val => getLabelByValue('OpportunityScore', val));
    
    loadedDashboards.forEach(dashboardName => {
      updateQSParameter("opportunityscore", opportunityLabels, dashboardName);
    });
  };

  const onRankPositionChange = (e) => {
    setSelectedRankPosition(e.target.value);
    
    loadedDashboards.forEach(dashboardName => {
      updateQSParameter("rankposition", [e.target.value.toString()], dashboardName);
    });
  };

  const onSegmentChange = (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
    setSelectedSegment(selected);
    
    const segmentLabels = selected.map(val => getLabelByValue('Segment', val));
    
    loadedDashboards.forEach(dashboardName => {
      updateQSParameter("segment", segmentLabels, dashboardName);
    });
  };

  const onHashtagFilterLettersChange = (checked) => {
    setHashtagFilterLetters(checked);
  };

  const onHashtagFilterNumbersChange = (checked) => {
    setHashtagFilterNumbers(checked);
  };

  const onHashtagFilterEmojisChange = (checked) => {
    setHashtagFilterEmojis(checked);
  };

  const onHashtagFilterEnglishChange = (checked) => {
    setHashtagFilterEnglish(checked);
  };

  const onDatehistoryChange = (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value[0] : e.target.value;
    setSelectedDatehistory([selected]);
    
    const match = filters?.DateRange?.options?.find(opt => opt.label === selected);
    if (match && match.start && match.end) {
      const start = dayjs.utc(match.start + 'T00:00:00');
      const end = dayjs.utc(match.end + 'T00:00:00');

      setSelectedDateRange([start, end]);
      setCustomStartDate(start);
      setCustomEndDate(end);
      setSelectedDateLabel(selected);
      
      loadedDashboards.forEach(dashboardName => {
        updateQSParameter("datestart", [start.format('YYYY-MM-DD')], dashboardName);
        updateQSParameter("dateend", [end.format('YYYY-MM-DD')], dashboardName);
        updateQSParameter("Datehistory", [selected], dashboardName);
      });
    }
  };

  const onCustomStartDateChange = (newDate) => {
    if (!newDate) {
      setCustomStartDate(null);
      return;
    }

    const parsedDate = dayjs.utc(newDate).startOf('day');
    setCustomStartDate(parsedDate);
    setSelectedDateRange([parsedDate, customEndDate]);
    
    if (customEndDate) {
      loadedDashboards.forEach(dashboardName => {
        updateQSParameter("datestart", [parsedDate.format('YYYY-MM-DD')], dashboardName);
      });
    }
  };

  const onCustomEndDateChange = (newDate) => {
    if (!newDate) {
      setCustomEndDate(null);
      return;
    }

    const parsedDate = dayjs.utc(newDate).startOf('day');
    
    if (customStartDate && parsedDate.isBefore(customStartDate)) {
      const newEndDate = customStartDate;
      setCustomEndDate(newEndDate);
      setSelectedDateRange([customStartDate, newEndDate]);
      
      loadedDashboards.forEach(dashboardName => {
        updateQSParameter("datestart", [customStartDate.format('YYYY-MM-DD')], dashboardName);
        updateQSParameter("dateend", [newEndDate.format('YYYY-MM-DD')], dashboardName);
      });
    } else {
      setCustomEndDate(parsedDate);
      setSelectedDateRange([customStartDate, parsedDate]);
      
      if (customStartDate) {
        loadedDashboards.forEach(dashboardName => {
          updateQSParameter("datestart", [customStartDate.format('YYYY-MM-DD')], dashboardName);
          updateQSParameter("dateend", [parsedDate.format('YYYY-MM-DD')], dashboardName);
        });
      }
    }
  };

  const onDateRangeChange = (e) => {
    const selected = Array.isArray(e.target.value) ? e.target.value[0] : e.target.value;
    setSelectedDateLabel(selected);

    const match = filters?.DateRange?.options?.find(opt => opt.label === selected);
    if (match && match.start && match.end) {
      const start = dayjs(match.start + 'T00:00:00').utc();
      const end = dayjs(match.end + 'T00:00:00').utc();

      setSelectedDateRange([start, end]);
      setCustomStartDate(start);
      setCustomEndDate(end);
      
      loadedDashboards.forEach(dashboardName => {
        updateQSParameter("datestart", [start.format('YYYY-MM-DD')], dashboardName);
        updateQSParameter("dateend", [end.format('YYYY-MM-DD')], dashboardName);
      });
    }
  };

  const onDateChange = (newDateRange) => {
    if (!newDateRange || !newDateRange[0] || !newDateRange[1]) {
        setSelectedDateRange([]);
        setCustomStartDate(null);
        setCustomEndDate(null);
        
        loadedDashboards.forEach(dashboardName => {
          updateQSParameter("datestart", [], dashboardName);
          updateQSParameter("dateend", [], dashboardName);
        });
        return;
    }

    const [start, end] = newDateRange;
    setSelectedDateRange([start, end]);
    setCustomStartDate(start);
    setCustomEndDate(end);
    
    loadedDashboards.forEach(dashboardName => {
      updateQSParameter("datestart", [start.format('YYYY-MM-DD')], dashboardName);
      updateQSParameter("dateend", [end.format('YYYY-MM-DD')], dashboardName);
    });
  };

  const onClearAllFilters = () => {
    const prior30Days = filters?.DateRange?.options?.find(
      opt => opt.label === "Prior 30 Days" || opt.value === "Prior 30 Days"
    );
    
    let start, end;
    
    if (prior30Days && prior30Days.start && prior30Days.end) {
      // Use the same parsing as setDefaultDates to ensure consistency
      start = dayjs.utc(prior30Days.start + 'T00:00:00');
      end = dayjs.utc(prior30Days.end + 'T00:00:00');
      setSelectedDatehistory([prior30Days.value]);
    } 

    setSelectedDateRange([start, end]);
    setCustomStartDate(start);
    setCustomEndDate(end);
    
    setSelectedCategory([]);
    setSelectedSubcategory([]);
    setSelectedHashtag([]);
    setSelectedLocation([]);
    setSelectedAge([]);
    setSelectedGender([]);
    setSelectedOpportunity([]);
    setSelectedSegment([]);
    setSelectedRankPosition(0);
    setSelectedDateLabel('');
    
    setHashtagFilterLetters(false);
    setHashtagFilterNumbers(false);
    setHashtagFilterEmojis(false);
    setHashtagFilterEnglish(true);

    setTimeout(() => {
      loadedDashboards.forEach(dashboardName => {
        updateQSParameter("datestart", [start.format('YYYY-MM-DD')], dashboardName);
        updateQSParameter("dateend", [end.format('YYYY-MM-DD')], dashboardName);
        updateQSParameter("categoryname", [], dashboardName);
        updateQSParameter("subcategory", [], dashboardName);
        updateQSParameter("hashtag", [], dashboardName);
        updateQSParameter("location", [], dashboardName);
        updateQSParameter("age", [], dashboardName);
        updateQSParameter("gender", [], dashboardName);
        updateQSParameter("opportunityscore", [], dashboardName);
        updateQSParameter("segment", [], dashboardName);
        updateQSParameter("rankposition", [], dashboardName);
        updateQSParameter("Datehistory", prior30Days ? [prior30Days.value] : [], dashboardName);
      });
    }, 100);
  };  

  useEffect(() => {
    if (!isInitialLoad && filters && datesInitialized) {
      if (loadedDashboards.has(activeDashboardId)) {
        console.log('📊 Dashboard already loaded:', activeDashboardId);
      } else {
        console.log('🆕 Loading new dashboard:', activeDashboardId);
        loadDashboard(activeDashboardId);
      }
    }
  }, [activeDashboardId]);

  if (!filters) return (
    <div className="min-h-screen flex items-center justify-center bg-black">
      <div className="loading-container">
        <p className="loading-text">Loading....</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen max-h-screen h-screen flex flex-col bg-black overflow-hidden">
        <Header className="h-[80px]" />

        <ThemeProvider theme={theme}>
        <div className="app-border-top" />
        <div className="flex flex-1 overflow-hidden relative min-h-0">
            <div className={`filters-container ${isFiltersPanelCollapsed ? 'collapsed' : 'expanded'}`}>
            <FiltersPanel
                filters={filters}
                selectedCategory={selectedCategory}
                selectedHashtag={selectedHashtag}
                selectedLocation={selectedLocation}
                selectedSubcategory={selectedSubcategory}
                selectedAge={selectedAge}
                selectedGender={selectedGender}
                selectedOpportunity={selectedOpportunity}
                selectedRankPosition={selectedRankPosition}
                selectedDateRange={selectedDateRange}
                selectedSegment={selectedSegment}
                selectedDateLabel={selectedDateLabel}
                selectedDatehistory={selectedDatehistory}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                hashtagFilterLetters={hashtagFilterLetters}
                hashtagFilterNumbers={hashtagFilterNumbers}
                hashtagFilterEmojis={hashtagFilterEmojis}
                hashtagFilterEnglish={hashtagFilterEnglish}
                onCategoryChange={handleCategoryChange}
                onHashtagChange={onHashtagChange}
                onLocationChange={onLocationChange}
                onSubcategoryChange={onSubcategoryChange}
                onAgeChange={onAgeChange}
                onGenderChange={onGenderChange}
                onOpportunityChange={onOpportunityChange}
                onRankPositionChange={onRankPositionChange}
                onSegmentChange={onSegmentChange}
                onDateChange={onDateChange}
                onDateRangeChange={onDateRangeChange}
                onDatehistoryChange={onDatehistoryChange}
                onCustomStartDateChange={onCustomStartDateChange}
                onCustomEndDateChange={onCustomEndDateChange}
                onHashtagFilterLettersChange={onHashtagFilterLettersChange}
                onHashtagFilterNumbersChange={onHashtagFilterNumbersChange}
                onHashtagFilterEmojisChange={onHashtagFilterEmojisChange}
                onHashtagFilterEnglishChange={onHashtagFilterEnglishChange}
                onClearAllFilters={onClearAllFilters}
                updateQSParameter={updateQSParameter}
                getAllSubcategories={getAllSubcategories}
                getFilteredHashtagOptions={getFilteredHashtagOptions}
                getFilteredSegmentOptions={getFilteredSegmentOptions}
            />
            </div>
            <div className="main-content">
                <div className="flex flex-col nav-border-bottom">
                    <Navigation activeTab={activeDashboardId} onTabChange={setActiveDashboardId} isFiltersPanelCollapsed={isFiltersPanelCollapsed} setIsFiltersPanelCollapsed={setIsFiltersPanelCollapsed} />
                </div>
            
              <div className="dashboard-container">
                {Object.keys(dashboardIdMap).map(dashboardName => {
                  if (dashboardName === 'A.I. Chat') {
                    return (
                      <div
                        key={dashboardName}
                        ref={chatContainerRef}
                        className={`dashboard-wrapper chat-container ${activeDashboardId === dashboardName ? 'active' : 'inactive'}`}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          overflow: 'hidden',
                        }}
                      >
                        <iframe
                          ref={chatIframeRef}
                          src="https://main.desmgcvtolrix.amplifyapp.com/"
                          title="A.I. Chat"
                          className="chat-iframe"
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                          }}
                        />
                      </div>
                    );
                  }
                  
                  return (
                    <div
                      key={dashboardName}
                      ref={el => {
                        if (containerRefs.current[dashboardName]) {
                          containerRefs.current[dashboardName].current = el;
                        }
                      }}
                      className={`dashboard-wrapper ${activeDashboardId === dashboardName ? 'active' : 'inactive'}`}
                    />
                  );
                })}
              </div>



            </div>
        </div>
        </ThemeProvider>
    </div>
    );
 }

export default withAuthenticator(DashboardEmbed);